import React, { useState, useEffect, memo } from "react";
import { 
  Save, 
  AlertCircle, 
  Loader2, 
  CalendarDays,
  PlusCircle,
  Trash2,
  Calendar,
  RefreshCw,
  MapPin,
  Edit2,
} from 'lucide-react';
import { fetchWithAuth, SEASON_EVENTS_API } from '../../../utils/api';

// Helper functions for date format conversion
const convertToUSDateFormat = (dateString) => {
  if (!dateString) return dateString;
  try {
    // Check if already in MM-DD-YYYY format
    if (dateString.includes('/') || dateString.includes('-') && dateString.length === 10 && dateString.indexOf('-') === 2) {
      return dateString; // Already in US format
    }
    
    // Convert from YYYY-MM-DD to MM-DD-YYYY
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${month}-${day}-${year}`;
    }
    return dateString;
  } catch (error) {
    console.error('Error converting date format:', error);
    return dateString;
  }
};

const convertFromUSDateFormat = (dateString) => {
  if (!dateString) return dateString;
  try {
    // Check if in MM-DD-YYYY format
    if (dateString.includes('-') && dateString.length === 10 && dateString.indexOf('-') === 2) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return dateString; // Return as-is if not in US format
  } catch (error) {
    console.error('Error converting from US date format:', error);
    return dateString;
  }
};

// Modal Components (moved outside main component to prevent re-rendering)
const SeasonModal = memo(({ 
  showSeasonModal, 
  setShowSeasonModal, 
  isEditingSeason, 
  newSeasonName, 
  setNewSeasonName, 
  newSeasonStart, 
  setNewSeasonStart, 
  newSeasonEnd, 
  setNewSeasonEnd, 
  handleAddSeason, 
  handleCancelEditSeason,
  modalError,
  setModalError
}) => (
  <div className={`modal ${showSeasonModal ? 'modal-open' : ''}`}>
    <div className="modal-box">
      <h3 className="font-bold text-lg mb-4">
        {isEditingSeason ? "Edit Season" : "Add New Season"}
      </h3>
      
      {/* Modal Error Display */}
      {modalError && (
        <div className="alert alert-error mb-4">
          <AlertCircle className="w-4 h-4" />
          <span>{modalError}</span>
        </div>
      )}
      
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">Season Name</span>
        </label>
        <input 
          type="text" 
          placeholder="e.g. '2024-2025'" 
          className="input input-bordered w-full" 
          value={newSeasonName}
          onChange={(e) => setNewSeasonName(e.target.value)}
        />
      </div>
      
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">Start Date</span>
        </label>
        <input 
          type="date" 
          className="input input-bordered w-full" 
          value={newSeasonStart}
          onChange={(e) => setNewSeasonStart(e.target.value)}
        />
      </div>
      
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">End Date</span>
        </label>
        <input 
          type="date" 
          className="input input-bordered w-full" 
          value={newSeasonEnd}
          onChange={(e) => setNewSeasonEnd(e.target.value)}
        />
      </div>
      
      <div className="modal-action">
        <button
          onClick={() => {
            const success = handleAddSeason();
            if (success) {
              setModalError('');
              setShowSeasonModal(false);
            }
          }}
          className="btn btn-primary"
        >
          {isEditingSeason ? (
            <>
              <Edit2 className="w-4 h-4 mr-1" />
              Update Season
            </>
          ) : (
            <>
              <PlusCircle className="w-4 h-4 mr-1" />
              Add Season
            </>
          )}
        </button>
        <button
          onClick={() => {
            handleCancelEditSeason();
            setModalError('');
            setShowSeasonModal(false);
          }}
          className="btn btn-outline"
        >
          Cancel
        </button>
      </div>
    </div>
    <div className="modal-backdrop" onClick={() => {
      handleCancelEditSeason();
      setModalError('');
      setShowSeasonModal(false);
    }}></div>
  </div>
));

const EventTypeModal = memo(({ 
  showEventTypeModal, 
  setShowEventTypeModal, 
  isEditingEventType, 
  newEventTypeName, 
  setNewEventTypeName, 
  newEventTypeDescription, 
  setNewEventTypeDescription, 
  newEventTypeHasCompetition, 
  setNewEventTypeHasCompetition, 
  newEventTypeHasConvention, 
  setNewEventTypeHasConvention, 
  newEventTypeHasTeachers, 
  setNewEventTypeHasTeachers, 
  newEventTypeHasObservers, 
  setNewEventTypeHasObservers, 
  handleAddEventType, 
  handleCancelEditEventType,
  modalError,
  setModalError
}) => (
  <div className={`modal ${showEventTypeModal ? 'modal-open' : ''}`}>
    <div className="modal-box">
      <h3 className="font-bold text-lg mb-4">
        {isEditingEventType ? "Edit Event Type" : "Add New Event Type"}
      </h3>
      
      {/* Modal Error Display */}
      {modalError && (
        <div className="alert alert-error mb-4">
          <AlertCircle className="w-4 h-4" />
          <span>{modalError}</span>
        </div>
      )}
      
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">Type Name</span>
        </label>
        <input 
          type="text" 
          placeholder="e.g. 'Regional', 'National', 'One-Day'" 
          className="input input-bordered w-full" 
          value={newEventTypeName}
          onChange={(e) => setNewEventTypeName(e.target.value)}
        />
      </div>
      
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">Description (Optional)</span>
        </label>
        <textarea 
          className="textarea textarea-bordered w-full" 
          placeholder="Brief description of this event type"
          value={newEventTypeDescription}
          onChange={(e) => setNewEventTypeDescription(e.target.value)}
        ></textarea>
      </div>
      
      {/* Add Toggles for Event Type Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-4">
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text text-sm">Includes Competition?</span> 
            <input 
              type="checkbox" 
              className="toggle toggle-sm toggle-primary" 
              checked={newEventTypeHasCompetition}
              onChange={(e) => setNewEventTypeHasCompetition(e.target.checked)} 
            />
          </label>
        </div>
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text text-sm">Includes Convention?</span> 
            <input 
              type="checkbox" 
              className="toggle toggle-sm toggle-primary" 
              checked={newEventTypeHasConvention}
              onChange={(e) => setNewEventTypeHasConvention(e.target.checked)} 
            />
          </label>
        </div>
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text text-sm">Includes Teachers?</span> 
            <input 
              type="checkbox" 
              className="toggle toggle-sm toggle-primary" 
              checked={newEventTypeHasTeachers}
              onChange={(e) => setNewEventTypeHasTeachers(e.target.checked)} 
            />
          </label>
        </div>
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text text-sm">Includes Observers?</span> 
            <input 
              type="checkbox" 
              className="toggle toggle-sm toggle-primary" 
              checked={newEventTypeHasObservers}
              onChange={(e) => setNewEventTypeHasObservers(e.target.checked)} 
            />
          </label>
        </div>
      </div>
      
      <div className="modal-action">
        <button
          onClick={() => {
            const success = handleAddEventType();
            if (success) {
              setModalError('');
              setShowEventTypeModal(false);
            }
          }}
          className="btn btn-primary"
        >
          {isEditingEventType ? (
            <>
              <Edit2 className="w-4 h-4 mr-1" />
              Update Event Type
            </>
          ) : (
            <>
              <PlusCircle className="w-4 h-4 mr-1" />
              Add Event Type
            </>
          )}
        </button>
        <button
          onClick={() => {
            handleCancelEditEventType();
            setModalError('');
            setShowEventTypeModal(false);
          }}
          className="btn btn-outline"
        >
          Cancel
        </button>
      </div>
    </div>
    <div className="modal-backdrop" onClick={() => {
      handleCancelEditEventType();
      setModalError('');
      setShowEventTypeModal(false);
    }}></div>
  </div>
));

const EventModal = memo(({ 
  showEventModal, 
  setShowEventModal, 
  isEditingEvent, 
  newEventName, 
  setNewEventName, 
  newEventSeason, 
  setNewEventSeason, 
  newEventCity, 
  setNewEventCity, 
  newEventStart, 
  setNewEventStart, 
  newEventEnd, 
  setNewEventEnd, 
  newEventType, 
  setNewEventType, 
  newEventRegistrationStatus, 
  setNewEventRegistrationStatus, 
  seasons, 
  eventTypes, 
  handleAddEvent, 
  handleCancelEditEvent,
  modalError,
  setModalError
}) => {
  // Find the selected season to get date constraints
  const selectedSeason = seasons.find(season => season.name === newEventSeason);
  const minDate = selectedSeason ? selectedSeason.startDate : '';
  const maxDate = selectedSeason ? selectedSeason.endDate : '';

  // Handle season change - clear dates if they're outside the new season range
  const handleSeasonChange = (seasonName) => {
    const newSelectedSeason = seasons.find(season => season.name === seasonName);
    setNewEventSeason(seasonName);
    
    if (newSelectedSeason && !isEditingEvent) {
      // Clear dates when changing season for new events
      setNewEventStart('');
      setNewEventEnd('');
    } else if (newSelectedSeason && isEditingEvent) {
      // For editing, validate current dates against new season
      const eventStartDate = new Date(newEventStart);
      const eventEndDate = new Date(newEventEnd);
      const seasonStartDate = new Date(newSelectedSeason.startDate);
      const seasonEndDate = new Date(newSelectedSeason.endDate);
      
      if (eventStartDate < seasonStartDate || eventStartDate > seasonEndDate) {
        setNewEventStart('');
      }
      if (eventEndDate < seasonStartDate || eventEndDate > seasonEndDate) {
        setNewEventEnd('');
      }
    }
  };

  return (
    <div className={`modal ${showEventModal ? 'modal-open' : ''}`}>
      <div className="modal-box">
        <h3 className="font-bold text-lg mb-4">
          {isEditingEvent ? "Edit Event" : "Add New Event"}
        </h3>
        
        {/* Modal Error Display */}
        {modalError && (
          <div className="alert alert-error mb-4">
            <AlertCircle className="w-4 h-4" />
            <span>{modalError}</span>
          </div>
        )}
        
        {/* Associated Season - moved to top */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text">Associated Season *</span>
          </label>
          <select
            className="select select-bordered w-full"
            value={newEventSeason}
            onChange={(e) => handleSeasonChange(e.target.value)}
          >
            <option value="" disabled selected={!newEventSeason}>Select a season first</option>
            {seasons.map((season, idx) => (
              <option key={idx} value={season.name}>
                {season.name}
              </option>
            ))}
          </select>
          {selectedSeason && (
            <label className="label">
              <span className="label-text-alt text-info">
                Events must be between {convertToUSDateFormat(selectedSeason.startDate).replace(/-/g, '/')} and {convertToUSDateFormat(selectedSeason.endDate).replace(/-/g, '/')}
              </span>
            </label>
          )}
        </div>
        
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text">Event Name</span>
          </label>
          <input 
            type="text" 
            placeholder="e.g. 'Fall Regional'" 
            className="input input-bordered w-full" 
            value={newEventName}
            onChange={(e) => setNewEventName(e.target.value)}
          />
        </div>
      
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">City</span>
        </label>
        <input 
          type="text" 
          placeholder="e.g. 'New York'" 
          className="input input-bordered w-full" 
          value={newEventCity}
          onChange={(e) => setNewEventCity(e.target.value)}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Start Date</span>
          </label>
          <input 
            type="date" 
            className={`input input-bordered w-full ${!selectedSeason ? 'input-disabled' : ''}`}
            value={newEventStart}
            min={minDate}
            max={maxDate}
            disabled={!selectedSeason}
            onChange={(e) => setNewEventStart(e.target.value)}
          />
          {!selectedSeason && (
            <label className="label">
              <span className="label-text-alt text-warning">Select a season first</span>
            </label>
          )}
        </div>
        
        <div className="form-control">
          <label className="label">
            <span className="label-text">End Date</span>
          </label>
          <input 
            type="date" 
            className={`input input-bordered w-full ${!selectedSeason ? 'input-disabled' : ''}`}
            value={newEventEnd}
            min={newEventStart || minDate}
            max={maxDate}
            disabled={!selectedSeason}
            onChange={(e) => setNewEventEnd(e.target.value)}
          />
          {!selectedSeason && (
            <label className="label">
              <span className="label-text-alt text-warning">Select a season first</span>
            </label>
          )}
        </div>
      </div>
      
      <div className="form-control mt-4">
        <label className="label">
          <span className="label-text">Event Type</span>
        </label>
        <select
          className="select select-bordered w-full"
          value={newEventType}
          onChange={(e) => setNewEventType(e.target.value)}
        >
          <option value="" disabled selected={!newEventType}>Select an event type</option>
          {eventTypes.map((type, idx) => (
            <option key={idx} value={type.name}>{type.name}</option>
          ))}
        </select>
      </div>
      
      <div className="form-control mt-4">
        <label className="label">
          <span className="label-text">Registration Status</span>
        </label>
        <select
          className="select select-bordered w-full"
          value={newEventRegistrationStatus}
          onChange={(e) => setNewEventRegistrationStatus(e.target.value)}
        >
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
          <option value="Waitlist">Waitlist</option>
        </select>
      </div>
      
      <div className="modal-action">
        <button
          onClick={() => {
            const success = handleAddEvent();
            if (success) {
              setModalError('');
              setShowEventModal(false);
            }
          }}
          className="btn btn-primary"
        >
          {isEditingEvent ? (
            <>
              <Edit2 className="w-4 h-4 mr-1" />
              Update Event
            </>
          ) : (
            <>
              <PlusCircle className="w-4 h-4 mr-1" />
              Add Event
            </>
          )}
        </button>
        <button
          onClick={() => {
            handleCancelEditEvent();
            setModalError('');
            setShowEventModal(false);
          }}
          className="btn btn-outline"
        >
          Cancel
        </button>
      </div>
    </div>
    <div className="modal-backdrop" onClick={() => {
      handleCancelEditEvent();
      setModalError('');
      setShowEventModal(false);
    }}></div>
  </div>
  );
});

/**
 * SeasonEventSettings
 *
 * Create new seasons, configure events, handle capacity limits, and deposits.
 * - Seasons: Start/end dates, naming
 * - Events: Tied to a season, capacity, deposit rules, etc.
 *
 * TailwindCSS styling for a consistent admin UI experience.
 */
const SeasonEventSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTab, setSelectedTab] = useState('seasons');
  
  // Modal states
  const [showSeasonModal, setShowSeasonModal] = useState(false);
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Modal error states
  const [seasonModalError, setSeasonModalError] = useState('');
  const [eventTypeModalError, setEventTypeModalError] = useState('');
  const [eventModalError, setEventModalError] = useState('');
  
  // ---------------------------
  // Seasons
  // ---------------------------
  const [seasons, setSeasons] = useState([]);

  // For creating a new season
  const [newSeasonName, setNewSeasonName] = useState("");
  const [newSeasonStart, setNewSeasonStart] = useState("");
  const [newSeasonEnd, setNewSeasonEnd] = useState("");
  const [isEditingSeason, setIsEditingSeason] = useState(false);
  const [editingSeasonIndex, setEditingSeasonIndex] = useState(null);

  // ---------------------------
  // Events
  // ---------------------------
  const [events, setEvents] = useState([]);

  // For creating a new event
  const [newEventName, setNewEventName] = useState("");
  const [newEventSeason, setNewEventSeason] = useState("");
  const [newEventCity, setNewEventCity] = useState("");
  const [newEventStart, setNewEventStart] = useState("");
  const [newEventEnd, setNewEventEnd] = useState("");
  const [newEventRegistrationStatus, setNewEventRegistrationStatus] = useState("Closed"); // Default to Closed
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [editingEventIndex, setEditingEventIndex] = useState(null);

  // ---------------------------
  // Event Types
  // ---------------------------
  const [eventTypes, setEventTypes] = useState([]);
  const [newEventType, setNewEventType] = useState("");

  // Event type creation/editing state
  const [newEventTypeName, setNewEventTypeName] = useState("");
  const [newEventTypeDescription, setNewEventTypeDescription] = useState("");
  const [newEventTypeHasCompetition, setNewEventTypeHasCompetition] = useState(true);
  const [newEventTypeHasConvention, setNewEventTypeHasConvention] = useState(true);
  const [newEventTypeHasTeachers, setNewEventTypeHasTeachers] = useState(true);
  const [newEventTypeHasObservers, setNewEventTypeHasObservers] = useState(true);
  const [isEditingEventType, setIsEditingEventType] = useState(false);
  const [editingEventTypeIndex, setEditingEventTypeIndex] = useState(null);

  // Set unsaved changes flag when any state changes
  // Note: We'll handle this manually in the relevant handlers instead of useEffect
  // to avoid unnecessary re-renders during typing

  // Add beforeunload event listener
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Add this useEffect to load data on component mount
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        // Call the API to get current settings
        const response = await fetchWithAuth(SEASON_EVENTS_API);
        
        // Update the component state with the returned data
        if (response) {
          // Convert dates from MM-DD-YYYY to YYYY-MM-DD for HTML date inputs
          const processedSeasons = (response.seasons || []).map(season => ({
            ...season,
            startDate: convertFromUSDateFormat(season.startDate),
            endDate: convertFromUSDateFormat(season.endDate)
          }));
          setSeasons(processedSeasons);
          
          // Process events and convert dates
          const processedEvents = (response.events || []).map(event => ({
            ...event,
            registrationStatus: event.registrationStatus || "Closed", // Default to "Closed"
            startDate: convertFromUSDateFormat(event.startDate),
            endDate: convertFromUSDateFormat(event.endDate)
          }));
          setEvents(processedEvents);
          // Process event types, adding default flags if missing
          const processedEventTypes = (response.eventTypes || []).map(type => ({
            ...type,
            hasCompetition: type.hasCompetition !== undefined ? type.hasCompetition : true,
            hasConvention: type.hasConvention !== undefined ? type.hasConvention : true,
            hasTeachers: type.hasTeachers !== undefined ? type.hasTeachers : true,
            hasObservers: type.hasObservers !== undefined ? type.hasObservers : true,
          }));
          setEventTypes(processedEventTypes);
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error("Error loading season and event settings:", error);
        setNotification({ 
          type: 'error', 
          message: 'Failed to load settings: ' + (error.message || 'Unknown error') 
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, []);

  // Add a custom CSS class to remove number input arrows
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Remove arrows from number inputs */
      input[type=number]::-webkit-inner-spin-button,
      input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      input[type=number] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ---------------------------
  // Handlers
  // ---------------------------
  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Convert dates to MM-DD-YYYY format before sending to backend
      const seasonsToSave = seasons.map(season => ({
        ...season,
        startDate: convertToUSDateFormat(season.startDate),
        endDate: convertToUSDateFormat(season.endDate)
      }));
      
      const eventsToSave = events.map(event => ({
        ...event,
        startDate: convertToUSDateFormat(event.startDate),
        endDate: convertToUSDateFormat(event.endDate)
      }));
      
      // Call the API with the converted data
      const response = await fetchWithAuth(SEASON_EVENTS_API, {
        method: 'PUT',
        body: {
          seasons: seasonsToSave,
          events: eventsToSave,
          eventTypes
        }
      });
      
      console.log("Season and Event Settings Saved:", response);
      setNotification({ type: 'success', message: 'Season and event settings saved successfully!' });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving season and event settings:", error);
      setNotification({ 
        type: 'error', 
        message: 'Failed to save settings: ' + (error.message || 'Unknown error') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    setIsLoading(true);
    try {
      // Call the API to get current settings
      const response = await fetchWithAuth(SEASON_EVENTS_API);
      
      // Update the component state with the returned data
      if (response) {
        // Convert dates from MM-DD-YYYY to YYYY-MM-DD for HTML date inputs
        const processedSeasons = (response.seasons || []).map(season => ({
          ...season,
          startDate: convertFromUSDateFormat(season.startDate),
          endDate: convertFromUSDateFormat(season.endDate)
        }));
        setSeasons(processedSeasons);
        
        const processedEvents = (response.events || []).map(event => ({
          ...event,
          startDate: convertFromUSDateFormat(event.startDate),
          endDate: convertFromUSDateFormat(event.endDate)
        }));
        setEvents(processedEvents);
        
        setEventTypes(response.eventTypes || []);
        setHasUnsavedChanges(false);
        setNotification({ type: 'success', message: 'Settings reset to saved values.' });
      }
    } catch (error) {
      console.error("Error resetting season and event settings:", error);
      setNotification({ 
        type: 'error', 
        message: 'Failed to reset settings: ' + (error.message || 'Unknown error') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSeason = () => {
    if (!newSeasonName || !newSeasonStart || !newSeasonEnd) {
      setSeasonModalError('Please fill all required fields for the new season.');
      return false;
    }
    
    if (isEditingSeason && editingSeasonIndex !== null) {
      // Update existing season
      const updatedSeasons = [...seasons];
      updatedSeasons[editingSeasonIndex] = {
        name: newSeasonName.trim(),
        startDate: newSeasonStart,
        endDate: newSeasonEnd,
      };
      setSeasons(updatedSeasons);
      setIsEditingSeason(false);
      setEditingSeasonIndex(null);
    } else {
      // Add new season
      const newSeason = {
        name: newSeasonName.trim(),
        startDate: newSeasonStart,
        endDate: newSeasonEnd,
      };
      setSeasons([...seasons, newSeason]);
    }
    
    setHasUnsavedChanges(true);
    
    // Reset form fields
    setNewSeasonName("");
    setNewSeasonStart("");
    setNewSeasonEnd("");
    return true;
  };

  const handleEditSeason = (index) => {
    const season = seasons[index];
    setNewSeasonName(season.name);
    setNewSeasonStart(season.startDate);
    setNewSeasonEnd(season.endDate);
    setIsEditingSeason(true);
    setEditingSeasonIndex(index);
  };

  const handleCancelEditSeason = () => {
    setNewSeasonName("");
    setNewSeasonStart("");
    setNewSeasonEnd("");
    setIsEditingSeason(false);
    setEditingSeasonIndex(null);
  };

  const handleRemoveSeason = (idx) => {
    const updated = [...seasons];
    updated.splice(idx, 1);
    setSeasons(updated);
    setHasUnsavedChanges(true);
  };

  const handleAddEventType = () => {
    if (!newEventTypeName) {
      setEventTypeModalError('Please enter a name for the event type.');
      return false;
    }
    
    if (isEditingEventType && editingEventTypeIndex !== null) {
      // Update existing event type
      const updatedEventTypes = [...eventTypes];
      updatedEventTypes[editingEventTypeIndex] = {
        name: newEventTypeName.trim(),
        description: newEventTypeDescription.trim(),
        hasCompetition: newEventTypeHasCompetition,
        hasConvention: newEventTypeHasConvention,
        hasTeachers: newEventTypeHasTeachers,
        hasObservers: newEventTypeHasObservers,
      };
      setEventTypes(updatedEventTypes);
      setIsEditingEventType(false);
      setEditingEventTypeIndex(null);
    } else {
      // Add new event type
      const newType = {
        name: newEventTypeName.trim(),
        description: newEventTypeDescription.trim(),
        hasCompetition: newEventTypeHasCompetition,
        hasConvention: newEventTypeHasConvention,
        hasTeachers: newEventTypeHasTeachers,
        hasObservers: newEventTypeHasObservers,
      };
      setEventTypes([...eventTypes, newType]);
    }
    
    setHasUnsavedChanges(true);
    
    // Reset form fields
    setNewEventTypeName("");
    setNewEventTypeDescription("");
    setNewEventTypeHasCompetition(true);
    setNewEventTypeHasConvention(true);
    setNewEventTypeHasTeachers(true);
    setNewEventTypeHasObservers(true);
    return true;
  };

  const handleEditEventType = (index) => {
    const eventType = eventTypes[index];
    setNewEventTypeName(eventType.name);
    setNewEventTypeDescription(eventType.description);
    setNewEventTypeHasCompetition(eventType.hasCompetition !== undefined ? eventType.hasCompetition : true);
    setNewEventTypeHasConvention(eventType.hasConvention !== undefined ? eventType.hasConvention : true);
    setNewEventTypeHasTeachers(eventType.hasTeachers !== undefined ? eventType.hasTeachers : true);
    setNewEventTypeHasObservers(eventType.hasObservers !== undefined ? eventType.hasObservers : true);
    setIsEditingEventType(true);
    setEditingEventTypeIndex(index);
  };

  const handleCancelEditEventType = () => {
    setNewEventTypeName("");
    setNewEventTypeDescription("");
    setNewEventTypeHasCompetition(true);
    setNewEventTypeHasConvention(true);
    setNewEventTypeHasTeachers(true);
    setNewEventTypeHasObservers(true);
    setIsEditingEventType(false);
    setEditingEventTypeIndex(null);
  };

  const handleRemoveEventType = (idx) => {
    const updated = [...eventTypes];
    updated.splice(idx, 1);
    setEventTypes(updated);
    setHasUnsavedChanges(true);
  };

  const handleAddEvent = () => {
    if (!newEventName || !newEventSeason || !newEventCity || !newEventStart || !newEventEnd || !newEventType || !newEventRegistrationStatus) {
      setEventModalError('Please fill out all required event details including event type and registration status.');
      return false;
    }

    // Find the selected season for date validation
    const selectedSeason = seasons.find(season => season.name === newEventSeason);
    if (!selectedSeason) {
      setEventModalError('Please select a valid season.');
      return false;
    }

    // Validate event dates are within season range
    const eventStartDate = new Date(newEventStart);
    const eventEndDate = new Date(newEventEnd);
    const seasonStartDate = new Date(selectedSeason.startDate);
    const seasonEndDate = new Date(selectedSeason.endDate);

    if (eventStartDate < seasonStartDate || eventStartDate > seasonEndDate) {
      setEventModalError(`Event start date must be between ${convertToUSDateFormat(selectedSeason.startDate).replace(/-/g, '/')} and ${convertToUSDateFormat(selectedSeason.endDate).replace(/-/g, '/')}.`);
      return false;
    }

    if (eventEndDate < seasonStartDate || eventEndDate > seasonEndDate) {
      setEventModalError(`Event end date must be between ${convertToUSDateFormat(selectedSeason.startDate).replace(/-/g, '/')} and ${convertToUSDateFormat(selectedSeason.endDate).replace(/-/g, '/')}.`);
      return false;
    }

    if (eventStartDate > eventEndDate) {
      setEventModalError('Event start date cannot be after the end date.');
      return false;
    }
    
    if (isEditingEvent && editingEventIndex !== null) {
      // Update existing event
      const updatedEvents = [...events];
      updatedEvents[editingEventIndex] = {
        name: newEventName.trim(),
        season: newEventSeason,
        eventType: newEventType,
        city: newEventCity.trim(),
        startDate: newEventStart,
        endDate: newEventEnd,
        registrationStatus: newEventRegistrationStatus,
      };
      setEvents(updatedEvents);
      setIsEditingEvent(false);
      setEditingEventIndex(null);
    } else {
      // Add new event
      const newEvt = {
        name: newEventName.trim(),
        season: newEventSeason,
        eventType: newEventType,
        city: newEventCity.trim(),
        startDate: newEventStart,
        endDate: newEventEnd,
        registrationStatus: newEventRegistrationStatus,
      };
      setEvents([...events, newEvt]);
    }
    
    setHasUnsavedChanges(true);
    
    // Reset form
    setNewEventName("");
    setNewEventSeason(seasons.length > 0 ? seasons[0].name : "");
    setNewEventType("");
    setNewEventCity("");
    setNewEventStart("");
    setNewEventEnd("");
    setNewEventRegistrationStatus("Closed");
    return true;
  };

  const handleEditEvent = (index) => {
    const event = events[index];
    setNewEventName(event.name);
    setNewEventSeason(event.season);
    setNewEventType(event.eventType || "");
    setNewEventCity(event.city);
    setNewEventStart(event.startDate);
    setNewEventEnd(event.endDate);
    setNewEventRegistrationStatus(event.registrationStatus || "Closed");
    setIsEditingEvent(true);
    setEditingEventIndex(index);
  };

  const handleCancelEditEvent = () => {
    setNewEventName("");
    setNewEventSeason(seasons.length > 0 ? seasons[0].name : "");
    setNewEventType("");
    setNewEventCity("");
    setNewEventStart("");
    setNewEventEnd("");
    setNewEventRegistrationStatus("Closed");
    setIsEditingEvent(false);
    setEditingEventIndex(null);
  };

  const handleRemoveEvent = (idx) => {
    const updated = [...events];
    updated.splice(idx, 1);
    setEvents(updated);
    setHasUnsavedChanges(true);
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="rounded-box p-6 bg-base-100 border border-base-300">
        <div className="flex flex-col lg:flex-row justify-between gap-6">
          <div>
            <h1 className="text-4xl font-bold text-base-content">
              Season & Event Settings
            </h1>
            <p className="text-base-content/60 mt-2">
              Create new seasons, configure events, and manage capacity limits
            </p>
          </div>
          <div className="flex items-center">
            <button
              onClick={handleReset}
              className="btn btn-outline mr-3"
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Reset
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading || !hasUnsavedChanges}
              onClick={handleSave}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`alert ${notification.type === 'error' ? 'alert-error' : 'alert-success'} flex items-center`}>
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-lg bg-primary/10">
              <CalendarDays className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="card-title text-lg font-semibold">Season/Event Type</h2>
              <p className="text-base-content/60 text-sm">Select which type to configure</p>
            </div>
          </div>
          
          <div className="tabs tabs-boxed">
            <a 
              className={`tab ${selectedTab === 'seasons' ? 'tab-active' : 'border border-base-300'}`}
              onClick={() => setSelectedTab('seasons')}
            >
              Seasons
            </a>
            <a 
              className={`tab ${selectedTab === 'eventtypes' ? 'tab-active' : 'border border-base-300'}`}
              onClick={() => setSelectedTab('eventtypes')}
            >
              Event Types
            </a>
            <a 
              className={`tab ${selectedTab === 'events' ? 'tab-active' : 'border border-base-300'}`}
              onClick={() => setSelectedTab('events')}
            >
              Events
            </a>
          </div>
        </div>
      </div>

      {/* Seasons Tab Content */}
      {selectedTab === 'seasons' && (
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-lg bg-primary/10">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="card-title text-lg font-semibold">Seasons</h2>
                <p className="text-base-content/60 text-sm">Manage your seasons for different events throughout the year</p>
              </div>
            </div>
            
            {/* Button to open Add Season Modal */}
            <div className="flex justify-end mb-6">
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => {
                  handleCancelEditSeason();
                  setSeasonModalError('');
                  setShowSeasonModal(true);
                }}
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Add New Season
              </button>
            </div>
            
            {/* Seasons Table */}
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Season Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {seasons.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8">
                        <div className="flex flex-col items-center justify-center text-base-content/60">
                          <Calendar className="w-8 h-8 mb-2 opacity-30" />
                          <p>No seasons defined yet</p>
                          <button 
                            className="btn btn-sm btn-primary mt-3" 
                            onClick={() => {
                              handleCancelEditSeason();
                              setShowSeasonModal(true);
                            }}
                          >
                            <PlusCircle className="w-4 h-4 mr-1" />
                            Add First Season
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    seasons.map((season, idx) => (
                      <tr key={idx}>
                        <td className="font-medium">{season.name}</td>
                        <td>{convertToUSDateFormat(season.startDate)}</td>
                        <td>{convertToUSDateFormat(season.endDate)}</td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                handleEditSeason(idx);
                                setSeasonModalError('');
                                setShowSeasonModal(true);
                              }}
                              className="btn btn-sm btn-square btn-outline btn-primary"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveSeason(idx)}
                              className="btn btn-sm btn-square btn-outline btn-error"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Event Types Tab Content */}
      {selectedTab === 'eventtypes' && (
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-lg bg-primary/10">
                <CalendarDays className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="card-title text-lg font-semibold">Event Types</h2>
                <p className="text-base-content/60 text-sm">Define different types of events and their durations</p>
              </div>
            </div>
            
            {/* Button to open Add Event Type Modal */}
            <div className="flex justify-end mb-6">
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => {
                  handleCancelEditEventType();
                  setShowEventTypeModal(true);
                }}
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Add New Event Type
              </button>
            </div>
            
            {/* Event Types Table */}
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Type Name</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {eventTypes.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8">
                        <div className="flex flex-col items-center justify-center text-base-content/60">
                          <CalendarDays className="w-8 h-8 mb-2 opacity-30" />
                          <p>No event types defined yet</p>
                          <button 
                            className="btn btn-sm btn-primary mt-3" 
                            onClick={() => {
                              handleCancelEditEventType();
                              setShowEventTypeModal(true);
                            }}
                          >
                            <PlusCircle className="w-4 h-4 mr-1" />
                            Add First Event Type
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    eventTypes.map((type, idx) => (
                      <tr key={idx}>
                        <td className="font-medium">{type.name}</td>
                        <td className="max-w-xs truncate">{type.description || "—"}</td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                handleEditEventType(idx);
                                setShowEventTypeModal(true);
                              }}
                              className="btn btn-sm btn-square btn-outline btn-primary"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveEventType(idx)}
                              className="btn btn-sm btn-square btn-outline btn-error"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Events Tab Content */}
      {selectedTab === 'events' && (
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-lg bg-primary/10">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="card-title text-lg font-semibold">Events</h2>
                <p className="text-base-content/60 text-sm">Configure events within a season</p>
              </div>
            </div>

            {seasons.length === 0 && (
              <div className="alert alert-warning mb-4">
                <AlertCircle className="w-4 h-4 mr-2" />
                <span>No seasons defined yet. Please add a season first on the Seasons tab.</span>
              </div>
            )}
            
            {/* Button to open Add Event Modal */}
            {seasons.length > 0 && (
              <div className="flex justify-end mb-6">
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    handleCancelEditEvent();
                    setNewEventSeason(seasons.length > 0 ? seasons[0].name : "");
                    setShowEventModal(true);
                  }}
                >
                  <PlusCircle className="w-4 h-4 mr-1" />
                  Add New Event
                </button>
              </div>
            )}
            
            {/* Events Table */}
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Season</th>
                    <th>City</th>
                    <th>Dates</th>
                    <th>Event Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center justify-center text-base-content/60">
                          <MapPin className="w-8 h-8 mb-2 opacity-30" />
                          <p>No events defined yet</p>
                          {seasons.length > 0 && (
                            <button 
                              className="btn btn-sm btn-primary mt-3" 
                              onClick={() => {
                                handleCancelEditEvent();
                                setNewEventSeason(seasons[0].name);
                                setShowEventModal(true);
                              }}
                            >
                              <PlusCircle className="w-4 h-4 mr-1" />
                              Add First Event
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    events.map((event, idx) => {
                      // Find the corresponding event type for this event
                      const eventTypeInfo = eventTypes.find(type => type.name === event.eventType);
                      
                      return (
                        <tr key={idx}>
                          <td className="font-medium">{event.name}</td>
                          <td>{event.season}</td>
                          <td>{event.city}</td>
                          <td>
                            <div className="flex flex-col">
                              <span>{convertToUSDateFormat(event.startDate)}</span>
                              <span className="text-xs text-base-content/60">to {convertToUSDateFormat(event.endDate)}</span>
                            </div>
                          </td>
                          <td>{event.eventType || "—"}</td>
                          <td>
                            <span className={`badge ${
                              event.registrationStatus === 'Open' ? 'badge-success' :
                              event.registrationStatus === 'Waitlist' ? 'badge-warning' :
                              'badge-error' // Default to Closed/Error badge
                            }`}>
                              {event.registrationStatus}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  handleEditEvent(idx);
                                  setShowEventModal(true);
                                }}
                                className="btn btn-sm btn-square btn-outline btn-primary"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRemoveEvent(idx)}
                                className="btn btn-sm btn-square btn-outline btn-error"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <SeasonModal 
        showSeasonModal={showSeasonModal}
        setShowSeasonModal={setShowSeasonModal}
        isEditingSeason={isEditingSeason}
        newSeasonName={newSeasonName}
        setNewSeasonName={setNewSeasonName}
        newSeasonStart={newSeasonStart}
        setNewSeasonStart={setNewSeasonStart}
        newSeasonEnd={newSeasonEnd}
        setNewSeasonEnd={setNewSeasonEnd}
        handleAddSeason={handleAddSeason}
        handleCancelEditSeason={handleCancelEditSeason}
        modalError={seasonModalError}
        setModalError={setSeasonModalError}
      />
      <EventTypeModal 
        showEventTypeModal={showEventTypeModal}
        setShowEventTypeModal={setShowEventTypeModal}
        isEditingEventType={isEditingEventType}
        newEventTypeName={newEventTypeName}
        setNewEventTypeName={setNewEventTypeName}
        newEventTypeDescription={newEventTypeDescription}
        setNewEventTypeDescription={setNewEventTypeDescription}
        newEventTypeHasCompetition={newEventTypeHasCompetition}
        setNewEventTypeHasCompetition={setNewEventTypeHasCompetition}
        newEventTypeHasConvention={newEventTypeHasConvention}
        setNewEventTypeHasConvention={setNewEventTypeHasConvention}
        newEventTypeHasTeachers={newEventTypeHasTeachers}
        setNewEventTypeHasTeachers={setNewEventTypeHasTeachers}
        newEventTypeHasObservers={newEventTypeHasObservers}
        setNewEventTypeHasObservers={setNewEventTypeHasObservers}
        handleAddEventType={handleAddEventType}
        handleCancelEditEventType={handleCancelEditEventType}
        modalError={eventTypeModalError}
        setModalError={setEventTypeModalError}
      />
      <EventModal 
        showEventModal={showEventModal}
        setShowEventModal={setShowEventModal}
        isEditingEvent={isEditingEvent}
        newEventName={newEventName}
        setNewEventName={setNewEventName}
        newEventSeason={newEventSeason}
        setNewEventSeason={setNewEventSeason}
        newEventCity={newEventCity}
        setNewEventCity={setNewEventCity}
        newEventStart={newEventStart}
        setNewEventStart={setNewEventStart}
        newEventEnd={newEventEnd}
        setNewEventEnd={setNewEventEnd}
        newEventType={newEventType}
        setNewEventType={setNewEventType}
        newEventRegistrationStatus={newEventRegistrationStatus}
        setNewEventRegistrationStatus={setNewEventRegistrationStatus}
        seasons={seasons}
        eventTypes={eventTypes}
        handleAddEvent={handleAddEvent}
        handleCancelEditEvent={handleCancelEditEvent}
        modalError={eventModalError}
        setModalError={setEventModalError}
      />
    </div>
  );
};

export default SeasonEventSettings;
